import React, { useEffect, useState } from 'react';
import { Container, Alert, Spinner, Badge } from 'react-bootstrap';
import { tableAPI } from '../api/api';
import './RelationshipVisualization.css';

function RelationshipVisualization() {
  const [tables, setTables] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showOnlyRelated, setShowOnlyRelated] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    fetchTableStructure();
  }, []);

  const fetchTableStructure = async () => {
    try {
      setLoading(true);
      // Removed debug logs for production
      
      // Fetch all actual database tables
      let tableList = [];
      
      try {
        const response = await tableAPI.getAllTables();
        
        tableList = response.data?.tables || response.data || [];
        
        // If response is already an array, use it directly
        if (Array.isArray(response.data)) {
          tableList = response.data;
        }
      } catch (e) {
        throw new Error('Unable to fetch tables from database: ' + e.message);
      }
      
      // Normalize table data - use tableName or TableName
      const normalizedTables = tableList.map(t => ({
        tableName: t.tableName || t.TableName,  // Keep camelCase to match FK data
        recordCount: t.recordCount || t.RecordCount || 0
      }));
      
      // Removed debug log for production
      setTables(normalizedTables);

      // Fetch all foreign keys in a single bulk request (much faster than individual calls)
      const foreignKeysResponse = await tableAPI.getAllForeignKeys();
      const allForeignKeys = foreignKeysResponse.data?.allForeignKeys || foreignKeysResponse.data?.foreignKeys || [];
      
      // Build relationships map from foreign keys
      const relsMap = new Map(); // Use map to deduplicate relationships
      
      allForeignKeys.forEach(fk => {
        // Create a unique key for each relationship to avoid duplicates
        const relKey = `${fk.localTable}→${fk.referencedTable}`;
        
        if (!relsMap.has(relKey)) {
          relsMap.set(relKey, {
            from: fk.localTable,
            to: fk.referencedTable,
            fields: []
          });
        }
        
        // Add field information to the relationship
        relsMap.get(relKey).fields.push({
          localColumn: fk.localColumn,
          referencedColumn: fk.referencedColumn
        });
      });
      
      const rels = Array.from(relsMap.values());
      
      // Removed debug logs for production
      setRelationships(rels);
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch table structure';
      setError(errorMsg);
      setRelationships([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="relationship-container">
        <div className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  // Filter tables based on relationships and search term
  const tablesWithRelationships = new Set();
  relationships.forEach(rel => {
    tablesWithRelationships.add(rel.from);
    tablesWithRelationships.add(rel.to);
  });

  let filteredTables = tables;
  
  // Filter by relationship if enabled
  if (showOnlyRelated && relationships.length > 0) {
    filteredTables = tables.filter(t => tablesWithRelationships.has(t.tableName));
  }
  
  // Filter by search term
  if (searchTerm.trim()) {
    filteredTables = filteredTables.filter(t => 
      t.tableName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // Filter relationships to only show those between visible tables
  const visibleTableNames = new Set(filteredTables.map(t => t.tableName));
  const visibleRelationships = relationships.filter(rel => 
    visibleTableNames.has(rel.from) && visibleTableNames.has(rel.to)
  );

  // Calculate SVG dimensions based on number of tables
  const colsPerRow = 4;
  const tableWidth = 200;
  const tableHeight = 100;
  const colSpacing = 280;
  const rowSpacing = 250;
  const padding = 150;
  
  const rows = Math.ceil(filteredTables.length / colsPerRow);
  const svgWidth = colsPerRow * colSpacing + padding + 50;
  const svgHeight = Math.max(800, rows * rowSpacing + 100);

  return (
    <Container fluid className="relationship-container">
      <div className="relationship-header">
        <h2>Database Relationship Map</h2>
        <p className="text-muted">Visual representation of table relationships and foreign keys</p>
        <div style={{ marginTop: '15px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="showRelated"
              checked={showOnlyRelated}
              onChange={(e) => setShowOnlyRelated(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="showRelated" style={{ cursor: 'pointer', marginBottom: 0 }}>
              Show only tables with relationships ({tablesWithRelationships.size} tables)
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="text"
              placeholder="Search tables..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontSize: '14px',
                minWidth: '200px'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
            <button
              onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                background: '#f8f9fa',
                cursor: 'pointer',
                fontSize: '12px'
              }}
              title="Zoom Out"
            >
              − Zoom Out
            </button>
            <span style={{ fontSize: '13px', minWidth: '60px', textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom(Math.min(3, zoom + 0.1))}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                background: '#f8f9fa',
                cursor: 'pointer',
                fontSize: '12px'
              }}
              title="Zoom In"
            >
              + Zoom In
            </button>
            <button
              onClick={() => setZoom(1)}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                background: '#f8f9fa',
                cursor: 'pointer',
                fontSize: '12px'
              }}
              title="Reset Zoom"
            >
              Reset
            </button>
          </div>
          <span style={{ fontSize: '13px', color: '#666' }}>
            Showing {filteredTables.length} of {tables.length} tables • {visibleRelationships.length} relationships
          </span>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="relationship-content">
        {filteredTables.length === 0 ? (
          <Alert variant="info">
            No tables found matching your criteria. {searchTerm && `Try searching for a different table or `} {showOnlyRelated && `uncheck "Show only tables with relationships" to see all tables.`}
          </Alert>
        ) : (
          <>
            {/* Graph View */}
            <div className="relationship-graph">
              <svg className="relationship-svg" viewBox={`0 0 ${svgWidth} ${svgHeight}`} width="100%" height="auto">
                <g style={{ transform: `scale(${zoom})`, transformOrigin: '0 0' }}>
                {/* Draw connection lines */}
                {visibleRelationships.map((rel, idx) => {
                  const fromTable = filteredTables.find(t => t.tableName === rel.from);
                  const toTable = filteredTables.find(t => t.tableName === rel.to);
                  
                  if (!fromTable || !toTable) return null;

                  const fromIndex = filteredTables.indexOf(fromTable);
                  const toIndex = filteredTables.indexOf(toTable);
                  
                  const fromX = 150 + (fromIndex % colsPerRow) * colSpacing;
                  const fromY = 100 + Math.floor(fromIndex / colsPerRow) * rowSpacing;
                  const toX = 150 + (toIndex % colsPerRow) * colSpacing;
                  const toY = 100 + Math.floor(toIndex / colsPerRow) * rowSpacing;

                  // Calculate the direction vector for the arrow
                  const dx = toX - fromX;
                  const dy = toY - fromY;
                  const distance = Math.sqrt(dx * dx + dy * dy);
                  const normX = distance > 0 ? dx / distance : 0;
                  const normY = distance > 0 ? dy / distance : 0;

                  // Curve the line for better visualization
                  const midX = (fromX + toX) / 2;
                  const midY = (fromY + toY) / 2;
                  const controlOffsetX = -normY * 50; // Perpendicular offset
                  const controlOffsetY = normX * 50;

                  return (
                    <g key={`rel-${idx}`} title={`${rel.from} → ${rel.to}`}>
                      {/* Connection curved line */}
                      <path
                        d={`M ${fromX + 100} ${fromY + 50} Q ${midX + controlOffsetX} ${midY + controlOffsetY} ${toX + 100} ${toY + 50}`}
                        stroke="#667eea"
                        strokeWidth="2"
                        fill="none"
                        opacity="0.6"
                      />
                      {/* Arrow head - proper triangle pointing toward the referenced table */}
                      <polygon
                        points={`${toX + 100},${toY + 50} ${toX + 90 - normX * 15},${toY + 40 - normY * 15} ${toX + 90 - normX * 15},${toY + 60 - normY * 15}`}
                        fill="#667eea"
                      />
                      {/* Relationship label showing field names */}
                      {rel.fields && rel.fields.length > 0 && (
                        <text
                          x={midX + controlOffsetX}
                          y={midY + controlOffsetY - 10}
                          fontSize="10"
                          fill="#667eea"
                          textAnchor="middle"
                          backgroundColor="white"
                          style={{ pointerEvents: 'none' }}
                        >
                          {rel.fields.map(f => `${f.localColumn}→${f.referencedColumn}`).join(', ')}
                        </text>
                      )}
                    </g>
                  );
                })}

                {/* Draw table boxes */}
                {filteredTables.map((table, idx) => {
                  const x = 150 + (idx % colsPerRow) * colSpacing;
                  const y = 100 + Math.floor(idx / colsPerRow) * rowSpacing;

                  return (
                    <g key={`table-${idx}`} className="table-node">
                      {/* Box */}
                      <rect
                        x={x}
                        y={y}
                        width="200"
                        height="100"
                        fill="white"
                        stroke="#667eea"
                        strokeWidth="2"
                        rx="6"
                      />
                      {/* Table name */}
                      <text
                        x={x + 100}
                        y={y + 25}
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="600"
                        fill="#333"
                      >
                        {table.tableName}
                      </text>
                      {/* Row count */}
                      <text
                        x={x + 100}
                        y={y + 50}
                        textAnchor="middle"
                        fontSize="12"
                        fill="#999"
                      >
                        {table.recordCount} records
                      </text>
                      {/* Relationships count */}
                      <text
                        x={x + 100}
                        y={y + 75}
                        textAnchor="middle"
                        fontSize="11"
                        fill="#667eea"
                      >
                        {visibleRelationships.filter(r => r.from === table.tableName || r.to === table.tableName).length} relationships
                      </text>
                    </g>
                  );
                })}
                </g>
              </svg>
            </div>

            {/* Legend */}
            <div className="relationship-legend">
              <h4>Legend</h4>
              <div className="legend-item">
                <div className="legend-box" style={{ borderColor: '#667eea' }}></div>
                <span>Table</span>
              </div>
              <div className="legend-item">
                <svg width="30" height="30" style={{ marginRight: '10px' }}>
                  <line x1="5" y1="15" x2="25" y2="15" stroke="#667eea" strokeWidth="2" strokeDasharray="5,5" />
                  <polygon points="25,15 20,10 20,20" fill="#667eea" />
                </svg>
                <span>Foreign Key Relationship</span>
              </div>
            </div>

            {/* Relationship Table */}
            {visibleRelationships.length > 0 && (
              <div className="relationship-table">
                <h4>Relationship Details</h4>
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>From Table</th>
                      <th>Local Column</th>
                      <th>Referenced Table</th>
                      <th>Referenced Column</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRelationships.map((rel, idx) => (
                      rel.fields && rel.fields.map((field, fieldIdx) => (
                        <tr key={`${idx}-${fieldIdx}`}>
                          <td>
                            <Badge bg="info">{rel.from}</Badge>
                          </td>
                          <td className="font-monospace">{field.localColumn}</td>
                          <td>
                            <Badge bg="success">{rel.to}</Badge>
                          </td>
                          <td className="font-monospace">{field.referencedColumn}</td>
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </Container>
  );
}

export default RelationshipVisualization;
